/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { Fragment } from 'react';

import { cssMap, jsx } from '@atlaskit/css';
import type { ExtensionParams } from '@atlaskit/editor-common/extensions/extension-handler';
import { token } from '@atlaskit/tokens';
import { useDarkModeColors } from '@atlassian/color-picker';
import { useProductDependencies } from '@atlassian/company-hub-common';
import { useHubAutoTextColor } from '@atlassian/company-hub-common/hub-auto-text-color';
import { useIsHubCornerRadiusEnabled } from '@atlassian/company-hub-common/use-is-hub-corner-radius-enabled';
import { useShouldShowPremiumUpsell } from '@atlassian/company-hub-common/use-should-show-premium-upsell';

import type { LinksMenuParameters } from '../../../types';
import { getLinksMenuStyles } from '../../../utils/get-link-menu-styles';
import { LinksMenuItems } from './links-menu-items';

type WithRequired<T, K extends keyof T> = T & { [P in K]-?: T[P] };
export type ExtensionNodeWithParameters = WithRequired<
	ExtensionParams<LinksMenuParameters>,
	'parameters'
>;
export const extensionHasParams = (
	extensionNode: ExtensionParams<LinksMenuParameters>,
): extensionNode is ExtensionNodeWithParameters => {
	return !!extensionNode.parameters;
};

export type LinksMenuContentProps = {
	contentId: string;
	extensionNode: ExtensionNodeWithParameters;
	isInEditor?: boolean;
};

const defaultStyles = cssMap({
	itemsWrapper: {
		display: 'grid',
		gridAutoFlow: 'column',
		paddingTop: token('space.150'),
		paddingBottom: token('space.150'),
		paddingInline: token('space.600'),
		justifyContent: 'center',
		gap: token('space.100'),
		minWidth: '100px',
	},
	// Spacing helps differentiate the extension container border
	// styles (showing that extension is selected to edit) vs the
	// internal border styles applied to links menu by user.
	editorStyles: {
		marginTop: token('space.050'),
		marginRight: token('space.050'),
		marginBottom: token('space.050'),
		marginLeft: token('space.050'),
	},
});

export const LinksMenuContent = ({
	extensionNode,
	isInEditor,
}: LinksMenuContentProps): JSX.Element => {
	const { parameters } = extensionNode;
	const { items, styles } = parameters;
	const { experienceSuccessComponent, isSSRProductInDarkMode } = useProductDependencies();
	const isCornerRadiusEnabled = useIsHubCornerRadiusEnabled();
	// Per-menu corner radius is Premium. The value lives in the ADF, so a downgrade
	// cannot clear it — suppressing the override makes the menu inherit the
	// hub-wide radius, which the backend resets to the 8px default.
	const isCornerRadiusLocked = useShouldShowPremiumUpsell();

	const { getDarkModeCompatibleBackgroundColor, getDarkModeCompatibleTextColor } =
		useDarkModeColors(isSSRProductInDarkMode);
	const fillColor = getDarkModeCompatibleBackgroundColor(styles.fillColor);

	// Keep black/white label and selected-state colours legible against the live
	// brand fill. Derived from the raw ADF fill rather than the dark-mode-adjusted
	// one so the palette lookup sees the stored value; explicit colours pass
	// through untouched. See `hub-auto-text-color`.
	const resolveTextColor = useHubAutoTextColor('links-menu-extension');
	const autoTextColor = resolveTextColor({
		backgroundColor: styles.fillColor,
		textColor: styles.textColor,
		defaultRef: 'primary.200',
	});
	const autoHighlightColor = resolveTextColor({
		backgroundColor: styles.fillColor,
		textColor: styles.highlightColor,
		defaultRef: 'primary.200',
	});

	const textColor = getDarkModeCompatibleTextColor(autoTextColor ?? styles.textColor);
	const highlightColor = getDarkModeCompatibleTextColor(
		autoHighlightColor ?? styles.highlightColor,
	);

	const ExperienceSuccessComponent = experienceSuccessComponent || Fragment;

	return (
		<div
			data-testid="links-menu-extension"
			css={[defaultStyles.itemsWrapper, isInEditor && defaultStyles.editorStyles]}
			// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop, @atlaskit/ui-styling-standard/no-imported-style-values
			style={getLinksMenuStyles(
				{ ...styles, fillColor },
				isCornerRadiusEnabled,
				isCornerRadiusLocked,
			)}
		>
			<LinksMenuItems highlightColor={highlightColor} items={items} textColor={textColor} />
			<ExperienceSuccessComponent />
		</div>
	);
};
